// Performance optimization helpers to reduce main thread blocking

/**
 * Break up long tasks using requestIdleCallback
 * @param {Function} task - The task to run
 * @param {Object} options - Options for the idle callback
 */
export function runWhenIdle(task, options = {}) {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(task, options);
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(task, 1);
  }
}

/**
 * Defer non-critical scripts until after page load
 * @param {Function} callback - The function to defer
 */
export function deferScript(callback) {
  if (document.readyState === 'complete') {
    // Page already loaded
    runWhenIdle(callback);
  } else {
    window.addEventListener('load', () => {
      runWhenIdle(callback);
    });
  }
}

/**
 * Load third-party scripts asynchronously with timeout
 * @param {string} src - Script source URL
 * @param {number} delay - Delay in milliseconds
 */
export function loadScriptDeferred(src, delay = 2000) {
  deferScript(() => {
    setTimeout(() => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }, delay);
  });
}

/**
 * Optimize Google Analytics/GTM loading
 */
export function loadAnalyticsDeferred() {
  // Only load analytics after crucial content is rendered
  deferScript(() => {
    // Google Tag Manager
    if (typeof window.dataLayer === 'undefined') {
      window.dataLayer = [];
    }
    
    loadScriptDeferred('https://www.googletagmanager.com/gtag/js?id=G-5X2J0MWRBL', 3000);
  });
}

/**
 * Break up expensive operations into chunks
 * @param {Array} items - Items to process
 * @param {Function} processor - Function to process each item
 * @param {number} chunkSize - Items per chunk
 */
export async function processInChunks(items, processor, chunkSize = 10) {
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    
    // Process chunk
    await Promise.all(chunk.map(processor));
    
    // Yield to main thread
    await new Promise(resolve => {
      runWhenIdle(() => resolve());
    });
  }
}

/**
 * Debounce function to prevent excessive calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 */
export function debounce(func, wait = 250) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}